/**
 * Product analytics: which screens get opened, and nothing else.
 *
 * A Nostr client is an unusually hostile place to put an analytics SDK, because the
 * data most SDKs collect by default is exactly the data this app spends its code
 * refusing to leak. Three rules therefore hold, and the configuration below exists
 * to enforce them rather than to be tuned later:
 *
 *  1. **No content, ever.** Autocapture records the text of clicked elements and the
 *     URL of the page they were on. Here that is note bodies, npubs, note ids and
 *     the contents of a private-message screen. It is off, session replay is off, and
 *     no page URL is ever sent — screens are reported by their route *name*, which
 *     is a fixed enum (`profile`, `messages`, …) and carries no pubkey or id.
 *  2. **No identity.** `identify()` is never called and person profiles are never
 *     created. Tying a pubkey to a session would hand a third party the one
 *     identifier this whole application is built around, and `relayAuth.ts` will not
 *     even give that to a *relay* without being asked to.
 *  3. **Nothing without a key.** With no `VITE_POSTHOG_KEY` the module is inert and
 *     the SDK is never even downloaded. That is the state of every fork, every
 *     self-hosted build and every `pnpm dev` — this is Apache-2.0 software, and a
 *     hardcoded key would silently point somebody else's users at our dashboard.
 *
 * The reader can also turn it off (`Settings → Privacy`), and Do Not Track is
 * honoured without being asked. The preference is stored under its own local key on
 * purpose: `DeviceSettings` is published to relays in the NIP-78 settings document,
 * and whether someone opted out of analytics is not something to broadcast.
 *
 * `posthog-js` is imported dynamically so a build with no key, or a reader who
 * opted out, never pays for the bytes.
 */

import type { PostHog } from "posthog-js";
import { useSyncExternalStore } from "react";

/** Where the opt-out lives. Local only — never in the synced settings document. */
const OPT_OUT_KEY = "setu-analytics-optout";

/** PostHog EU cloud, since that is where the project lives. Overridable. */
const DEFAULT_HOST = "https://eu.i.posthog.com";

/** The inputs that decide whether anything is sent at all. */
export interface AnalyticsEnvironment {
  /** `VITE_POSTHOG_KEY`, absent in dev, in forks and in self-hosted builds. */
  readonly key: string | undefined;
  /** False under `pnpm dev`, where events would be noise in a real project. */
  readonly production: boolean;
  /** `navigator.doNotTrack === "1"`. */
  readonly doNotTrack: boolean;
  /** The reader's own choice, from `Settings → Privacy`. */
  readonly optedOut: boolean;
}

/**
 * Whether analytics may run. Pure, so the rule is testable and stays in one place.
 *
 * Every condition is a veto: a key must exist, the build must be a real one, and
 * neither the browser nor the reader may have said no.
 */
export function analyticsAllowed(env: AnalyticsEnvironment): boolean {
  if (env.key === undefined || env.key === "") return false;
  if (!env.production) return false;
  if (env.doNotTrack) return false;
  if (env.optedOut) return false;
  return true;
}

function storage(): Storage | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    // Disabled by policy, or a partitioned context that throws on access.
    return undefined;
  }
}

/** True when this reader has turned analytics off. */
export function analyticsOptedOut(): boolean {
  return storage()?.getItem(OPT_OUT_KEY) === "1";
}

const listeners = new Set<() => void>();

/**
 * Records the choice and applies it immediately.
 *
 * Turning it off calls `opt_out_capturing`, which stops the SDK that is already
 * running rather than leaving it to stop at the next reload — an opt-out that takes
 * effect later is not one.
 */
export function setAnalyticsOptedOut(optedOut: boolean): void {
  try {
    if (optedOut) storage()?.setItem(OPT_OUT_KEY, "1");
    else storage()?.removeItem(OPT_OUT_KEY);
  } catch {
    // Unwritable storage: honour the choice for this session at least.
  }
  if (optedOut) client?.opt_out_capturing();
  else void startAnalytics();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The opt-out, re-rendering when it changes. */
export function useAnalyticsOptedOut(): boolean {
  return useSyncExternalStore(subscribe, analyticsOptedOut, () => true);
}

/** True when this build has analytics available to turn on at all. */
export function analyticsConfigured(): boolean {
  return currentEnvironment().key !== undefined;
}

function currentEnvironment(): AnalyticsEnvironment {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  return {
    key: typeof key === "string" && key !== "" ? key : undefined,
    production: import.meta.env.PROD,
    doNotTrack:
      typeof navigator !== "undefined" &&
      (navigator as { doNotTrack?: string }).doNotTrack === "1",
    optedOut: analyticsOptedOut(),
  };
}

let client: PostHog | undefined;
let starting: Promise<void> | undefined;

/**
 * Loads and configures the SDK, once, if it is allowed to run.
 *
 * Idempotent and safe to call from anywhere: the opt-out toggle calls it to start a
 * session that was never started, and boot calls it unconditionally.
 */
export function startAnalytics(): Promise<void> {
  if (client !== undefined) {
    client.opt_in_capturing();
    return Promise.resolve();
  }
  if (starting !== undefined) return starting;
  const env = currentEnvironment();
  if (!analyticsAllowed(env)) return Promise.resolve();

  starting = import("posthog-js")
    .then(({ posthog }) => {
      const host = import.meta.env.VITE_POSTHOG_HOST;
      posthog.init(env.key as string, {
        api_host: typeof host === "string" && host !== "" ? host : DEFAULT_HOST,
        // Rule 1: nothing about the page or its contents.
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        disable_surveys: true,
        // Rule 2: no person, so nothing to tie to a pubkey.
        person_profiles: "never",
        /*
         * Nothing is written to the device, which keeps this out of consent
         * territory in the EU and matches what the About screen promises. The cost
         * is real and worth stating: without a stored id, a returning reader counts
         * as a new one, so these are screen-view counts and never unique visitors.
         */
        persistence: "memory",
        /*
         * The CSP is `script-src 'self'`, so any extension the SDK tried to fetch
         * from the CDN would be blocked by the browser anyway. Declining to ask is
         * better than a console full of violations — and it keeps the CSP intact
         * rather than widening it for features that are all switched off above.
         */
        disable_external_dependency_loading: true,
        // No flags are used, so the request that fetches them is pure traffic.
        advanced_disable_feature_flags: true,
        /*
         * The address bar is stripped from every event, and this is the guard that
         * has to survive future changes rather than the one that matters today.
         *
         * `capture_pageview: false` does not stop it: the SDK attaches
         * `$current_url`, `$pathname` and `$host` to *everything* it sends, as an
         * inspection of the payload showed. Right now that is harmless — routes
         * here live in memory, so the URL is always the site root. But `routes.ts`
         * says a URL-backed router "lands with the desktop shell", and on the day it
         * does, every event would start carrying npubs and note ids without anyone
         * editing this file or noticing. Denied at the source instead.
         *
         * `$raw_user_agent` goes with them: PostHog already derives the browser and
         * OS fields that are actually useful, and the raw string is a
         * fingerprinting surface bought for nothing. The referrer stays — it says
         * how people found a public web app and contains nothing of the reader's.
         *
         * `$host` stays too, and denying it was a mistake worth recording. It is the
         * deployment's own domain and can never hold a route parameter, so there was
         * nothing to protect — while PostHog's own "internal and test users" filter
         * is written against it. Without the property that filter cannot match, so
         * every real event was excluded from insights and the dashboard read zero
         * while the raw activity feed listed the events arriving.
         */
        property_denylist: [
          "$current_url",
          "$pathname",
          "$raw_user_agent",
          // The same address under two more names, which is why this list was
          // written against an actual captured payload rather than from the docs.
          "$session_entry_url",
          "$session_entry_pathname",
        ],
      });
      client = posthog;
      flushPending();
    })
    .catch(() => {
      // A blocked or failed SDK load is not an error a reader can act on, and
      // analytics must never be a reason the app does not start.
      starting = undefined;
    });
  return starting;
}

/**
 * Screens opened before the SDK finished loading.
 *
 * The SDK arrives over the network, so it is never ready when the first screen
 * mounts — and dropping that one loses the arrival screen of every session, which is
 * the single most useful thing here. Buffered only while a load is genuinely in
 * flight: with no key, or after an opt-out, `starting` is never set and nothing
 * accumulates. The cap is for the pathological case of a load that never settles.
 */
const pending: string[] = [];
const MAX_PENDING = 10;

/**
 * Reports that a screen was opened.
 *
 * `name` must be a route *name* — `profile`, `messages` — never a path or a title.
 * The route model carries a pubkey, a hashtag and a community address in its other
 * fields, and none of them belong in an event.
 */
export function trackScreen(name: string): void {
  if (client !== undefined) {
    /*
     * Sent under both names, and the `$`-prefixed one is not decoration.
     *
     * PostHog's own Activity table and its screen-based insights read
     * `$screen_name` (or `$current_url`, which is denied above). With only a custom
     * property the events arrive and the "Url / Screen" column reads "—" for every
     * row, which looks exactly like a broken integration. `screen` stays because it
     * is the name this app chose and the one its own queries would use.
     */
    client.capture("screen_view", { screen: name, $screen_name: name });
    return;
  }
  if (starting !== undefined && pending.length < MAX_PENDING) {
    pending.push(name);
  }
}

function flushPending(): void {
  const queued = pending.splice(0, pending.length);
  for (const name of queued) trackScreen(name);
}
